class StickyQuickBuy extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.mainPicker = null;
    this.observer = null;
    this.resizeHandler = null;
    this.stickyPicker = null;
    this.productForm = null;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.mainPicker = this.findMainPicker();
    this.stickyPicker = this.querySelector('variant-picker');
    this.productForm = this.querySelector('product-form form');
    this.setListeners();
    this.observeMainPicker();
    this.syncButtonState(this.getCurrentVariant());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.resizeHandler && window.removeEventListener('resize', this.resizeHandler);
  }

  setListeners() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-select-variant]')?.addEventListener('click', () => this.toggleVariantPicker(), { signal });
    this.querySelector('[data-change-variant]')?.addEventListener('click', () => this.toggleVariantPicker(), { signal });
    this.querySelector('[data-close-variants]')?.addEventListener('click', () => this.closeVariantPicker(true), { signal });
    document.addEventListener('click', this.onDocumentClick, { signal });
    document.addEventListener('keydown', this.onKeydown, { signal });
    // A variant change re-renders the product info and replaces the main picker: keep observing the live one.
    document.addEventListener('variant:change', () => requestAnimationFrame(() => this.refreshMainPicker()), { signal });
    this.productForm?.addEventListener('variant:change', this.onVariantChange, { signal });
    this.productForm?.addEventListener('product:rerender', (event) => this.onProductRerender(event), { signal });
    this.querySelector('product-form')?.addEventListener('variant:add', () => this.resetModal(), { signal });
  }

  onVariantChange(event) {
    this.syncButtonState(event.detail?.variant);

    // Once every option has a value the panel has done its job: close it so it stops covering the page.
    if (this.hasAttribute('variant-selected') && this.isVariantPickerOpen()) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = setTimeout(() => this.closeVariantPicker(), 250);
    }
  }

  onDocumentClick(event) {
    if (this.isVariantPickerOpen() && !event.composedPath().includes(this)) {
      this.closeVariantPicker();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isVariantPickerOpen()) {
      this.closeVariantPicker(true);
    }
  }

  onProductRerender(event) {
    const formId = this.productForm?.getAttribute('id');

    if (event.detail?.productChange || !formId) {
      return;
    }

    const selector = `variant-picker[form-id="${CSS.escape(formId)}"]`;
    const newPicker = event.detail.htmlFragment.querySelector(selector);
    const currentPicker = this.querySelector(selector);

    if (!newPicker || !currentPicker) {
      return;
    }

    currentPicker.replaceWith(newPicker);
    this.stickyPicker = newPicker;
    this.syncButtonState(this.getCurrentVariant());
  }

  getCurrentVariant() {
    const variantScript =
      this.stickyPicker?.querySelector('script[data-variant]') || this.querySelector('script[data-sticky-variant]');

    return JSON.parse(variantScript?.textContent || 'null');
  }

  syncButtonState(variant) {
    const options = this.stickyPicker?.querySelectorAll('.variant-picker__option') || [];
    const selected = Array.from(options).every((option) => option.querySelector('input[data-option-position]:checked'));

    this.toggleAttribute('variant-selected', selected);
    this.toggleAttribute('variant-available', selected && variant?.available === true);

    const changeLabel = this.querySelector('[data-change-variant-label]');

    if (changeLabel) {
      changeLabel.textContent = selected && variant ? variant.public_title || variant.title : '';
    }
  }

  findMainPicker() {
    return document.querySelector(
      `variant-picker[context="main_product"][product-id="${CSS.escape(this.dataset.productId)}"]`
    );
  }

  refreshMainPicker() {
    const picker = this.findMainPicker();

    if (picker && picker !== this.mainPicker) {
      this.mainPicker = picker;
      this.buildObserver();
    }
  }

  observeMainPicker() {
    if (!this.mainPicker) {
      this.setActive(true);
      return;
    }

    this.buildObserver();
    this.resizeHandler = () => this.buildObserver();
    window.addEventListener('resize', this.resizeHandler);
  }

  buildObserver() {
    this.observer?.disconnect();

    const stickyHeight = this.querySelector('[data-content]')?.offsetHeight || 0;
    this.observer = new IntersectionObserver(
      (entries) => this.setActive(!entries[0].isIntersecting),
      { rootMargin: `0px 0px -${stickyHeight}px 0px`, threshold: 0 }
    );
    this.observer.observe(this.mainPicker);
  }

  setActive(visible) {
    if (visible) {
      this.removeAttribute('hiding');
      this.setAttribute('active', '');
      return;
    }

    if (!this.hasAttribute('active')) {
      return;
    }

    this.closeVariantPicker();
    this.removeAttribute('active');
    this.setAttribute('hiding', '');
    this.addEventListener(
      'transitionend',
      (event) => event.propertyName === 'opacity' && this.removeAttribute('hiding'),
      { once: true }
    );
  }

  resetModal() {
    this.closeVariantPicker();
  }

  isVariantPickerOpen() {
    return this.hasAttribute('variants-open');
  }

  toggleVariantPicker() {
    if (this.isVariantPickerOpen()) {
      this.closeVariantPicker(true);
    } else {
      this.openVariantPicker();
    }
  }

  openVariantPicker() {
    clearTimeout(this.closeTimeout);
    this.querySelector('[data-variants]')?.setAttribute('open', '');
    this.setAttribute('variants-open', '');
    this.updateExpanded(true);
  }

  closeVariantPicker(restoreFocus = false) {
    clearTimeout(this.closeTimeout);

    if (!this.isVariantPickerOpen()) {
      return;
    }

    this.querySelector('[data-variants]')?.removeAttribute('open');
    this.removeAttribute('variants-open');
    this.updateExpanded(false);

    if (restoreFocus) {
      const trigger = this.hasAttribute('variant-selected')
        ? this.querySelector('[data-change-variant]')
        : this.querySelector('[data-select-variant]');
      trigger?.focus({ preventScroll: true });
    }
  }

  updateExpanded(expanded) {
    this.querySelectorAll('[data-select-variant], [data-change-variant]').forEach((button) =>
      button.setAttribute('aria-expanded', String(expanded))
    );
  }
}

if (!window.customElements.get('sticky-quick-buy')) {
  window.customElements.define('sticky-quick-buy', StickyQuickBuy);
}
